import { useState } from "react";
import { toast } from "sonner";
import { Lock, ShieldCheck, LogOut, Smartphone, MonitorSmartphone } from "lucide-react";
import { PinInput } from "@/components/PinInput";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { LOGO_URL } from "@/lib/format";

const Shell = ({ icon: Icon, title, sub, children }) => {
  const { user, logout } = useAuth();
  return (
    <div className="max-w-md mx-auto min-h-screen bg-white px-7 pt-14 pb-10 flex flex-col sm:shadow-2xl" data-testid="pin-screen">
      <img src={LOGO_URL} alt="CU Tuah Talino" className="h-24 w-24 mx-auto rounded-full object-contain bg-white keep-white" />
      <div className="mx-auto mt-6 h-12 w-12 rounded-full bg-red-50 flex items-center justify-center"><Icon className="h-6 w-6 text-red-600" /></div>
      <h1 className="text-2xl font-bold text-center mt-3 text-slate-900" data-testid="pin-screen-title">{title}</h1>
      <p className="text-sm text-center text-slate-600 mt-1">{sub}</p>
      <p className="text-xs text-center text-slate-500 mt-1" data-testid="pin-screen-email">{user.email}</p>
      <div className="mt-8 space-y-5">{children}</div>
      <button onClick={logout} data-testid="pin-logout-button" className="mt-auto pt-8 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-red-600">
        <LogOut className="h-4 w-4" /> Keluar / ganti akun
      </button>
    </div>
  );
};

export function PinVerify() {
  const { setUser } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (value) => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/pin", { pin: value });
      setUser(r.data);
    } catch (e) {
      setError(errMsg(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell icon={Lock} title="Masukkan PIN" sub="Masukkan 6 digit PIN yang terdaftar di CU Tuah Talino">
      <PinInput value={pin} onChange={setPin} onComplete={submit} disabled={busy} testid="pin-verify-input" />
      {error && <p className="text-sm text-center text-red-600 font-medium" data-testid="pin-error">{error}</p>}
      <button onClick={() => submit(pin)} disabled={busy || pin.length !== 6} data-testid="pin-verify-submit"
        className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl transition-colors">
        {busy ? "Memeriksa..." : "Masuk"}
      </button>
      <p className="text-xs text-center text-slate-500">Lupa PIN? Hubungi admin CU untuk mengatur ulang PIN.</p>
    </Shell>
  );
}

export function DeviceConflict() {
  const { user, setUser, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const takeover = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/device/takeover");
      setUser(r.data);
      toast.success("Perangkat lain telah dikeluarkan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell icon={Smartphone} title="Akun Aktif di Perangkat Lain" sub="Satu akun hanya bisa digunakan di 1 perangkat. Pilih perangkat yang ingin tetap digunakan.">
      <div className="space-y-2" data-testid="device-conflict-list">
        {(user.other_devices || []).map((d, i) => (
          <div key={i} className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <MonitorSmartphone className="h-6 w-6 text-amber-600 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold text-slate-900">{d.device}</div>
              <div className="text-xs text-slate-500">Aktif terakhir {new Date(d.last_seen).toLocaleString("id-ID")}{d.ip ? ` • IP ${d.ip}` : ""}</div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={takeover} disabled={busy} data-testid="device-takeover-button"
        className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition-colors">
        {busy ? "Memproses..." : "Keluarkan Perangkat Lain & Lanjut di Sini"}
      </button>
      <button onClick={logout} data-testid="device-cancel-button" className="w-full border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold py-3.5 rounded-xl transition-colors">
        Batal, Tetap di Perangkat Lain
      </button>
      <p className="text-xs text-center text-slate-500">Jika itu bukan Anda, segera ganti PIN setelah masuk.</p>
    </Shell>
  );
}

export function PinChangeForced() {
  const { setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (value) => {
    if (value !== pin) {
      toast.error("Konfirmasi PIN tidak sama");
      setConfirm("");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/pin/change", { new_pin: pin });
      setUser(r.data);
      toast.success("PIN baru berhasil disimpan");
    } catch (e) {
      toast.error(errMsg(e));
      setStep(0);
      setPin("");
      setConfirm("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell icon={ShieldCheck} title={step === 0 ? "Buat PIN Baru" : "Ulangi PIN Baru"} sub="Demi keamanan, ganti PIN awal dari admin dengan PIN pribadi Anda">
      {step === 0 ? (
        <PinInput key="new" value={pin} onChange={setPin} onComplete={() => setStep(1)} testid="pin-new-input" />
      ) : (
        <PinInput key="confirm" value={confirm} onChange={setConfirm} onComplete={save} disabled={busy} testid="pin-confirm-input" />
      )}
      {step === 1 && (
        <button onClick={() => { setStep(0); setPin(""); setConfirm(""); }} data-testid="pin-change-restart" className="w-full text-sm text-blue-800 font-semibold">Ulangi dari awal</button>
      )}
      <p className="text-xs text-center text-slate-500">Hindari PIN mudah ditebak seperti 123456 atau tanggal lahir.</p>
    </Shell>
  );
}
